import { attachmentService } from './attachmentService';
import { SelectedFile } from '../components/forms/FileUpload';

export interface FileUploadProgress {
  section: string;
  fileName: string;
  progress: number; // 0-100
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

export interface FileUploadResult {
  success: boolean;
  uploadedCount: number;
  failedCount: number;
  errors: string[];
  uploadedFiles: Array<{
    section: string;
    fileName: string;
    attachmentId: number;
  }>;
}

/**
 * Upload a single file with retry logic
 */
const uploadSingleFileWithRetry = async (
  selectedFile: SelectedFile,
  requestId: number,
  section: string,
  onProgress?: (progress: FileUploadProgress) => void,
  maxRetries: number = 3,
): Promise<{ success: boolean; attachmentId?: number; error?: string }> => {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(
        `🔗 Upload attempt ${attempt}/${maxRetries} for file: ${selectedFile.name}`,
      );

      // Report upload start
      onProgress?.({
        section,
        fileName: selectedFile.name,
        progress: 0,
        status: 'uploading',
      });

      // Upload to server with progress tracking
      const uploadResult = await attachmentService.saveAttachment(
        selectedFile.file,
        requestId,
        section,
        'upload',
        selectedFile.caption,
        selectedFile.thumbnail,
        selectedFile.thumbnailMimeType,
        uploadProgress => {
          onProgress?.({
            section,
            fileName: selectedFile.name,
            progress: uploadProgress,
            status: 'uploading',
          });
        },
      );

      if (uploadResult.success && uploadResult.attachment) {
        console.log(
          `✅ Successfully uploaded ${selectedFile.name} on attempt ${attempt}`,
        );
        return {
          success: true,
          attachmentId: uploadResult.attachment.id,
        };
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.warn(
        `❌ Upload attempt ${attempt}/${maxRetries} failed for ${selectedFile.name}:`,
        lastError.message,
      );

      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // 1s, 2s, 4s, max 5s
        console.log(`⏳ Waiting ${delayMs}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  // All retries failed
  return {
    success: false,
    error: lastError?.message || 'Upload failed after multiple attempts',
  };
};

/**
 * Upload all selected files for a specific request
 */
export const uploadFilesForRequest = async (
  requestId: number,
  filesBySection: Record<string, SelectedFile[]>,
  onProgress?: (progress: FileUploadProgress) => void,
): Promise<FileUploadResult> => {
  console.log(
    '🔗 DEBUGGING: fileUploadService.uploadFilesForRequest called with requestId:',
    requestId,
    '(type:',
    typeof requestId,
    ')',
  );

  const result: FileUploadResult = {
    success: true,
    uploadedCount: 0,
    failedCount: 0,
    errors: [],
    uploadedFiles: [],
  };

  const allSections = Object.keys(filesBySection);
  console.log('🔗 DEBUGGING: Sections to process:', allSections);

  for (const section of allSections) {
    const files = filesBySection[section];

    for (const selectedFile of files) {
      const uploadResult = await uploadSingleFileWithRetry(
        selectedFile,
        requestId,
        section,
        onProgress,
        3, // 3 retry attempts
      );

      if (uploadResult.success && uploadResult.attachmentId) {
        result.uploadedCount++;
        result.uploadedFiles.push({
          section,
          fileName: selectedFile.name,
          attachmentId: uploadResult.attachmentId,
        });

        // Report success
        onProgress?.({
          section,
          fileName: selectedFile.name,
          progress: 100,
          status: 'completed',
        });
      } else {
        result.failedCount++;
        result.success = false;
        const errorMessage = uploadResult.error || 'Unknown error';
        result.errors.push(`${selectedFile.name}: ${errorMessage}`);

        // Report error
        onProgress?.({
          section,
          fileName: selectedFile.name,
          progress: 0,
          status: 'error',
          error: errorMessage,
        });
      }
    }
  }

  return result;
};

/**
 * Get total file count and size across all sections
 */
export const getFileStats = (
  filesBySection: Record<string, SelectedFile[]>,
) => {
  let totalCount = 0;
  let totalSize = 0;

  Object.values(filesBySection).forEach(files => {
    totalCount += files.length;
    totalSize += files.reduce((sum, file) => sum + file.size, 0);
  });

  return {
    totalCount,
    totalSize,
    totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100,
  };
};

export const fileUploadService = {
  uploadFilesForRequest,
  getFileStats,
};
